import { Link, useLocation } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";
import logoDark from "@/assets/logo-dark.svg";
import { gtmEnabled, openConsentSettings } from "@/lib/consent";

// Same destinations as the header navigation, plus the FAQ, so the footer is a
// full second way around the site.
const sectionLinks = [
  { label: "Services", hash: "#services" },
  { label: "How We Deliver", hash: "#deliver" },
  { label: "India", hash: "#india" },
  { label: "Global", hash: "#global" },
  { label: "About", hash: "#about" },
  { label: "FAQ", hash: "#faq" },
];

// Footer links carry min-h-[24px] + py-1 so the tap target meets the 24×24px
// minimum (WCAG 2.5.8 — NCS-252 DF16).
const linkClass =
  "inline-flex items-center min-h-[24px] py-1 text-sm hover:text-footer-foreground transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-footer-foreground rounded-sm";

const Footer = () => {
  const { pathname } = useLocation();
  const onHome = pathname === "/";
  // Bare "#india" only anchors on the home page; elsewhere navigate home first.
  const sectionHref = (hash: string) => (onHome ? hash : `/${hash}`);

  return (
    // The footer is dark in both themes (see --footer), so it always uses the
    // lockup made for dark backgrounds.
    <footer className="bg-footer px-6 border-t border-footer-foreground/10 md:px-8 lg:px-16 pb-24 md:pb-12 pt-12 text-footer-foreground/70 md:pt-14" aria-label="Site footer">
      <div className="max-w-7xl mx-auto grid gap-10 md:grid-cols-3">
        <div>
          <Link to="/" className="inline-block rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-footer-foreground" aria-label="AccessBharat — Home">
            <img src={logoDark} alt="" width={320} height={72} className="h-12 w-auto" aria-hidden="true" />
          </Link>
          <p className="mt-4 max-w-xs text-sm leading-relaxed">
            Web accessibility audits, remediation and WCAG compliance for India and global standards.
          </p>
        </div>

        <nav aria-label="Footer navigation">
          <h2 className="mb-3 font-display text-sm font-bold text-footer-foreground">Explore</h2>
          <ul className="grid grid-cols-2 gap-x-6 gap-y-1 list-none">
            {sectionLinks.map((l) => (
              <li key={l.hash}>
                <a href={sectionHref(l.hash)} className={linkClass}>{l.label}</a>
              </li>
            ))}
            <li>
              <Link to="/blog" className={linkClass}>Blog</Link>
            </li>
          </ul>
        </nav>

        <div>
          <h2 className="mb-3 font-display text-sm font-bold text-footer-foreground">Get in touch</h2>
          <ul className="space-y-2 list-none">
            <li className="flex items-center gap-2 text-sm">
              <Mail size={16} className="shrink-0" aria-hidden="true" />
              <a href="mailto:priya@accessbharat.com" className={linkClass}>priya@accessbharat.com</a>
            </li>
            <li className="flex items-center gap-2 text-sm">
              <MapPin size={16} className="shrink-0" aria-hidden="true" />
              <span>Mumbai, Maharashtra</span>
            </li>
            <li>
              <a href={sectionHref("#contact")} className="mt-2 inline-flex min-h-[44px] items-center rounded-md border border-footer-foreground/40 px-4 text-sm font-medium text-footer-foreground transition-colors hover:bg-footer-foreground/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-footer-foreground">
                Schedule Audit
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-10 flex flex-col gap-3 border-t border-footer-foreground/20 pt-6 text-sm md:flex-row md:items-start md:justify-between">
        <p>© {new Date().getFullYear()} AccessBharat. WCAG compliance India | Web accessibility audit | Enterprise accessibility solutions.</p>
        <ul className="flex shrink-0 list-none flex-wrap gap-x-6">
          <li>
            <Link to="/privacy" className={linkClass}>Privacy policy</Link>
          </li>
          {gtmEnabled() && (
            <li>
              <button type="button" onClick={openConsentSettings} className={linkClass}>Cookie settings</button>
            </li>
          )}
        </ul>
      </div>
    </footer>
  );
};

export default Footer;
